import React, { useState, useEffect, useMemo } from 'react';
import { Search, X, Package } from 'lucide-react';
import { crudOperations } from '../services/api';

const ProductServicePicker = ({
  selectedProductName,
  onProductSelect,
  disabled = false,
  label = 'Product/Service *',
  compact = false
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);

  // Load products on mount
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        const response = await crudOperations.get('/products-services');
        const all = response.data?.data?.products || response.data?.products || [];
        setProducts(all.filter(p => p.is_active));
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, []);

  // Filter products based on search term
  const filteredProducts = useMemo(() => {
    if (!searchTerm.trim()) return products;
    const search = searchTerm.toLowerCase();
    return products.filter(p =>
      p.name?.toLowerCase().includes(search) ||
      p.sales_description?.toLowerCase().includes(search) ||
      p.category_level1?.toLowerCase().includes(search) ||
      p.item_code?.toLowerCase().includes(search)
    );
  }, [products, searchTerm]);

  const handleOpen = () => {
    if (!disabled && !loading) {
      setShowModal(true);
      setSearchTerm('');
    }
  };

  const handleSelect = (product) => {
    setShowModal(false);
    setSearchTerm('');
    onProductSelect(product);
  };

  const handleClose = () => {
    setShowModal(false);
    setSearchTerm('');
  };

  if (compact) {
    return (
      <>
        <div className="relative">
          <input
            type="text"
            value={selectedProductName || ''}
            onClick={handleOpen}
            readOnly
            disabled={disabled || loading}
            className={`w-full px-2.5 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue text-sm cursor-pointer pr-8 ${
              disabled ? 'bg-gray-100 cursor-not-allowed' : 'hover:border-gray-400'
            }`}
            placeholder={loading ? 'Loading...' : 'Select product...'}
          />
          {selectedProductName ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onProductSelect(null); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={14} />
            </button>
          ) : (
            <Search size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          )}
        </div>

        {showModal && (
          <PickerModal
            products={filteredProducts}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onSelect={handleSelect}
            onClose={handleClose}
            selectedProductName={selectedProductName}
          />
        )}
      </>
    );
  }

  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <div className="relative">
        <input
          type="text"
          value={selectedProductName || ''}
          onClick={handleOpen}
          readOnly
          disabled={disabled || loading}
          className={`w-full px-2.5 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue text-sm cursor-pointer pr-8 ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : 'hover:border-gray-400'
          }`}
          placeholder={loading ? 'Loading...' : 'Select product...'}
        />
        {selectedProductName ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onProductSelect(null); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X size={14} />
          </button>
        ) : (
          <Search size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        )}
      </div>

      {showModal && (
        <PickerModal
          products={filteredProducts}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onSelect={handleSelect}
          onClose={handleClose}
          selectedProductName={selectedProductName}
        />
      )}
    </div>
  );
};

const PickerModal = ({ products, searchTerm, onSearchChange, onSelect, onClose, selectedProductName }) => (
  <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-[60] overflow-hidden" onClick={onClose}>
    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden m-4 flex flex-col" onClick={(e) => e.stopPropagation()}>
      {/* Header */}
      <div className="flex justify-between items-center p-4 md:p-6 border-b border-gray-200 bg-gradient-to-r from-pest-blue to-pest-teal">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-white">Select Product / Service</h2>
          <p className="text-sm text-white/90 mt-1">
            {products.length} product{products.length !== 1 ? 's' : ''} available — Click to select
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-white hover:text-gray-200 transition-colors duration-200"
          aria-label="Close product selection"
        >
          <X size={24} />
        </button>
      </div>

      {/* Search */}
      <div className="p-4 md:p-6 border-b border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors"
            placeholder="Search products by name, description or category..."
            autoFocus
          />
        </div>
      </div>

      {/* Products list */}
      <div className="p-4 md:p-6 overflow-y-auto flex-1 overscroll-contain">
        {/* Custom entry option when search has text */}
        {searchTerm.trim() && (
          <button
            type="button"
            onClick={() => onSelect({ name: searchTerm.trim(), sales_description: '', price: 0 })}
            className="w-full text-left p-4 rounded-lg border-2 border-dashed border-pest-blue bg-blue-50 hover:bg-blue-100 transition-all duration-200 mb-3"
          >
            <div className="flex items-center gap-2">
              <span className="text-pest-blue font-semibold">Use "{searchTerm.trim()}"</span>
              <span className="text-sm text-gray-500">as custom entry</span>
            </div>
          </button>
        )}

        {products.length > 0 ? (
          <div className="space-y-2">
            {products.map((product) => (
              <button
                key={product.id}
                type="button"
                onClick={() => onSelect(product)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 hover:bg-blue-50 hover:border-blue-300 ${
                  selectedProductName === product.name
                    ? 'bg-blue-100 border-blue-400'
                    : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900">
                      {product.name}
                    </div>
                    {product.sales_description && (
                      <div className="text-sm text-gray-600 mt-1 line-clamp-2">
                        {product.sales_description}
                      </div>
                    )}
                    {product.category_level1 && (
                      <div className="text-xs text-pest-teal mt-1">
                        {[product.category_level1, product.category_level2, product.category_level3].filter(Boolean).join(' > ')}
                      </div>
                    )}
                  </div>
                  {parseFloat(product.price) > 0 && (
                    <div className="text-base font-bold text-pest-blue ml-4 shrink-0">
                      £{parseFloat(product.price).toFixed(2)}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        ) : !searchTerm.trim() ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-2">No products found</p>
          </div>
        ) : null}
      </div>

      {/* Footer with Cancel */}
      <div className="p-4 border-t border-gray-200 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
);

export default ProductServicePicker;
